import type { BankCard } from '@/features/bankCard';
import type {
  CreditProductType,
  OceanCreditProductName,
} from '@/features/offer/types/offer';

export interface ContractResponse {
  id: string;
  alfaTrustValid: boolean;
  contractLink: string;
  application: Application;
}

export interface Application {
  id: string;
  creditProductName: string;
  localizedCreditProductName: string;
  amount: number;
  availableAmount: number;
  debtAmount: number;
  grantingCommission: number;
  interestAmount: number;
  nominalInterestPercentage: number;
  annualPercentageRate: number;
  dailyPercentageRate: number;
  dailyFee: number;
  daysCount: number;
  monthsCount: number;
  state: number;
  firstBillDate: Date;
  identification: string | null;
  address: string | null;
  bankCard: BankCard;
  creditProductType: OceanCreditProductName;
  creditType: CreditProductType;
  origin: number;
  allowPartnerManagement: boolean;
  merchantRepaymentOption: string | null;
  refinancedLoans: unknown[]; // Adjust to proper type if refinanced loan structure is defined
  hasRefinancedLoans: boolean;
  finalOffersState: string | null;
  isExpired: boolean;
  offerAvailable: boolean;
  isBankCardRequired: boolean;
  minNumberOfRates: number;
  maxNumberOfRates: number;
}

export interface LoanContract {
  id: string;
  number: string;
  state: ContractStatus;
  application: Application;
  text: string | null;
  moneySend: MoneySend;
  payments: LoanPayment[]; // Adjust type as necessary
  hasPendingTransactions: boolean;
  hasPendingCardTransactions: boolean;
  hasPendingMerchantOrders: boolean;
  monthlyWithdrawalsAvailable: number;
  canWithdraw: boolean;
  merchantOrders: unknown[]; // Adjust type as necessary
  lastMerchantOrder: unknown | null; // Adjust type as necessary
  productType: CreditProductType;
  signedAt: Date; // Use `Date` if converted to Date type
  firstPaymentDate: Date; // Use `Date` if converted to Date type
  nextPaymentDate: Date; // Use `Date` if converted to Date type
  lastPaymentDate: Date; // Use `Date` if converted to Date type
  stateDueDate: unknown | null; // Adjust type as necessary
  creditLineStateDueDate: unknown | null; // Adjust type as necessary
  areCreditLineBillsPaid: boolean;
  isRefinanceAvailable: number;
  debtGroups: DebtGroup[];
  reschedulingAvailable: number;
  isReschedulingAvailable: boolean;
  amountToClose: number;
  interestRecalculationDiscount: number;
  contractDueDate: string; // Use `Date` if converted to Date type
  daysUntilFinalDue: number;
  creditLineState: number;
  isProlongationAvailable: boolean;
  prolongationAvailability: number;
  partnerName: string | null;
  isWithdrawalEnabled: boolean;
  debtAmount: number;
  effectiveAnnualPercentageRate?: number;
  nominalAnnualPercentageRate?: number;
  prolongationAvailableCount: number;
  prolongationIsAvailable: number;
}

export const ContractStatus = {
  Draft: 'Draft',
  ReadyForMoneySend: 'ReadyForMoneySend',
  ReadyForSign: 'ReadyForSign',
  Signed: 'Signed',
  Closed: 'Closed',
  Cancelled: 'Cancelled',
  Collection: 'Collection',
  Executed: 'Executed',
  OutOfBalance: 'OutOfBalance',
  Fraud: 'Fraud',
} as const;

export type ContractStatus =
  (typeof ContractStatus)[keyof typeof ContractStatus];

export interface LoanContractsHistory {
  id: string;
  timestamp: Date;
  amount: number;
  totalAmount: number;
  productName: OceanCreditProductName;
  localizedProductName: string;
  period: number;
  periodUnit: string;
  interestTotalPayedAmount: number;
  closedTimestamp?: Date;
  interrest: number;
  dae: number;
  type: string;
  allowToDownload: boolean;
}

export interface LoanContractSummary {
  creditContractId: string;
  productLimit: number;
  totalAmount: number;
  balanceAmount: number;
  availableAmount: number;
  dailyInterestPercentage: number;
  state: ContractStatus;
  calculationDay: number;
  firstBillDate: Date;
  dailyCost: number;
  productType: CreditProductType;
}

export interface LoanLimits {
  applicationLimitState: ApplicationLimitState;
  expireIn: number;
  durationDays: number;
  contractLimit: number;
  closedContracts: number;
}

export const ApplicationLimitState = {
  NoRestriction: 0,
  Penultimate: 1,
  Restricted: 2,
} as const;

export type ApplicationLimitState =
  (typeof ApplicationLimitState)[keyof typeof ApplicationLimitState];

export interface CreditLineOngoingDetails {
  daysUsed: number;
  commission: number;
  amountUsed: number;
  interest: number;
  penaltyInterest: number;
  requiredAmount: number;
  billAmount: number;
  totalAmount: number;
}

export interface LoanContractResponse {
  bankCards: BankCard[];
  contract: LoanContract;
  creditLineOngoingDetails: CreditLineOngoingDetails;
}

export interface LoanPayment {
  orderId?: string;
  orderNo?: string;
  merchantCui?: string;
  merchantName?: string;
  interestAmount?: number;
  penaltyAmount?: number;
  commissionAmount?: number;
  paymentType: LoanPaymentType;
  id: string;
  timestamp?: Date; // Consider Date
  amount: number;
  amountSign?: string;
  currency: string;
  bankCardMask?: string;
  status: PaymentStatus;
}

export interface LoanPaymentTransaction {
  id: string;
  timestamp?: Date;
  amount: number;
  sign: string;
  currency: string;
  orderId?: string;
  orderNo?: string;
  merchantCui?: string;
  merchantName?: string;
  status: PaymentStatus;
  type: LoanPaymentType;
}

export interface MoneySend {
  id: string;
  timestamp: Date;
  amount: number;
  bankCardMask: string;
  status: PaymentStatus;
}

export const PaymentStatus = {
  Pending: 0,
  Success: 1,
  Fail: 2,
  Expired: 3,
} as const;

export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const LoanPaymentType = {
  Prolongation: 1,
  Contract: 2,
  Refinancing: 3,
  MoneySend: 4,
  Refund: 5,
  CreditLineClosure: 6,
  Withdrawal: 7,
  WlcOrderRefund: 8,
  ExternalRefinance: 9,
  MerchantOrder: 10,
} as const;

export type LoanPaymentType =
  (typeof LoanPaymentType)[keyof typeof LoanPaymentType];

export interface MonthGroupedItems<T> {
  month: string;
  summary: string;
  items: T[];
}

export interface NextPaymentInfo {
  capital?: number;
  interest?: number;
  totalAmount?: number;
  penalties?: number;
  date: Date;
}

export interface DebtGroup {
  startDate: Date;
  dueDate: Date;
  overdueHours: number;
  interest: number;
  commission: number;
  penalty: number;
  total: number;
  isPaid: boolean;
  isNext: boolean;
  isFirst: boolean;
  isLast: boolean;
}

// What the user declares when signing the contract with the code.
export interface ContractSignDeclarations {
  isRealBeneficiary: boolean;
  isPoliticallyExposed: boolean;
}
